import React from 'react';
import {
  Eye,
  Download,
  X,
  Image as ImageIcon,
  FileText as FilePdfIcon,
  Paperclip
} from 'lucide-react';
import { formatZAR } from '../../utils/currency';

const IncidentDetailsModal = ({
  selectedIncident,
  onClose,
  onDownloadAttachment,
  onPreviewAttachment,
  getSeverityColor,
  getStatusColor,
  getRiskColor
}) => {
  if (!selectedIncident) return null;

  const handlePreviewAttachment = (attachment) => {
    const previewableAttachments = selectedIncident.attachments.filter(a => a.content_type?.startsWith('image/') || a.content_type === 'application/pdf');
    const previewIndex = previewableAttachments.findIndex(a => a.id === attachment.id);
    if (previewIndex > -1) {
      onPreviewAttachment({
        attachments: previewableAttachments,
        index: previewIndex
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-2xl font-bold text-gray-900">Incident Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Incident Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Title</p>
              <h3 className="text-lg font-semibold text-gray-900">{selectedIncident.title}</h3>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Location</p>
              <p className="text-gray-800">{selectedIncident.location_description}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Type</p>
              <p className="text-gray-800 capitalize">{selectedIncident.incident_type.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Reported On</p>
              <p className="text-gray-800">{new Date(selectedIncident.created_at).toLocaleDateString()}</p>
            </div>
          </div>

          {/* Status & Severity Badges */}
          <div className="flex flex-wrap gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSeverityColor(selectedIncident.severity)}`}>
              Severity: {selectedIncident.severity}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedIncident.status)}`}>
              Status: {selectedIncident.status}
            </span>
            {selectedIncident.risk_score && (
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(selectedIncident.risk_score)}`}>
                Risk Score: {selectedIncident.risk_score}
              </span>
            )}
          </div>

          {/* Description */}
          <div>
            <p className="text-sm font-medium text-gray-500">Description</p>
            <p className="text-gray-800 whitespace-pre-wrap">{selectedIncident.description}</p>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedIncident.immediate_actions_taken && (
              <div>
                <p className="text-sm font-medium text-gray-500">Immediate Actions Taken</p>
                <p className="text-gray-800 whitespace-pre-wrap">{selectedIncident.immediate_actions_taken}</p>
              </div>
            )}
            {selectedIncident.estimated_damage_cost && (
              <div>
                <p className="text-sm font-medium text-gray-500">Estimated Damage Cost</p>
                <p className="text-gray-800">{formatZAR(selectedIncident.estimated_damage_cost)}</p>
              </div>
            )}
            {selectedIncident.affected_people > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-500">People Affected</p>
                <p className="text-gray-800">{selectedIncident.affected_people}</p>
              </div>
            )}
            {selectedIncident.witnesses && selectedIncident.witnesses.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-500">Witnesses</p>
                <p className="text-gray-800">{selectedIncident.witnesses.join(', ')}</p>
              </div>
            )}
            {selectedIncident.latitude && selectedIncident.longitude && (
              <div>
                <p className="text-sm font-medium text-gray-500">GPS Coordinates</p>
                <p className="text-gray-800">{selectedIncident.latitude.toFixed(6)}, {selectedIncident.longitude.toFixed(6)}</p>
              </div>
            )}
          </div>

          {/* Attachments */}
          {selectedIncident.attachments && selectedIncident.attachments.length > 0 && (
            <div>
              <p className="text-sm font-medium text-gray-500 mb-3">Attachments ({selectedIncident.attachments.length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {selectedIncident.attachments.map((attachment, index) => {
                  const isPreviewable = attachment.content_type && (attachment.content_type.startsWith('image/') || attachment.content_type === 'application/pdf');
                  return (
                    <div key={attachment.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-2 overflow-hidden">
                        {attachment.content_type?.startsWith('image/') ? (
                          <ImageIcon size={18} className="text-gray-500 flex-shrink-0" />
                        ) : attachment.content_type === 'application/pdf' ? (
                          <FilePdfIcon size={18} className="text-red-500 flex-shrink-0" />
                        ) : (
                          <Paperclip size={18} className="text-gray-500 flex-shrink-0" />
                        )}
                        <span className="text-sm text-gray-800 truncate" title={attachment.filename}>
                          {attachment.filename || `Attachment ${index + 1}`}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {isPreviewable && (
                          <button
                            type="button"
                            onClick={() => handlePreviewAttachment(attachment)}
                            className="p-1 text-cyan-600 hover:text-cyan-800"
                            title="Preview"
                          >
                            <Eye size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => onDownloadAttachment(attachment)}
                          className="p-1 text-gray-600 hover:text-gray-800"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncidentDetailsModal;
